import { createContext, ReactNode, useContext } from 'react';
import { useApolloClient } from '@apollo/client';
import { DiscoverQueries } from '../../common/graphql';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { getChartSpecs } from '../VizUtil.common';
import { messages } from '../../i18n';
import { VIZ_SELECTORS } from '../../common/redux/selectors/viz-selectors';
import {
  IFilterWidgetContext,
  ISearchOptions,
} from './filter-widget.interfaces';

type Props = {
  children: ReactNode;
};

export const FilterWidgetContext = createContext<IFilterWidgetContext>({
  loading: false,
  data: {
    visualizations: [],
  },
  selectedTypes: {
    tags: [],
    chartTypes: [],
    datasets: [],
  },
  labelDisplayMap: {},
});

export const FilterWidgetContextProvider = ({ children }: Props) => {
  const client = useApolloClient();

  const labelDisplayMap = {
    chartTypes: _.mapValues(getChartSpecs(), val =>
      _.get(messages, val.name, val.name),
    ),
  };

  let loading = false;
  let data: ISearchOptions | undefined;

  try {
    data = client.readQuery<ISearchOptions>({
      query: DiscoverQueries.SearchOptions,
    });
  } catch {
    // If data is not in cache, we could set loading to true or handle as needed
    loading = false;
    data = undefined;
  }

  const chartTypes = useSelector(({ tables }: any) => {
    return tables.library.chartTypes || [];
  });
  const datasets = useSelector(
    ({ tables }: any) => tables.library.searchDatasets || [],
  );
  const availableTags = useSelector(
    ({ tables }: any) => tables.library.tags || [],
  );
  const discoveryId = useSelector(state =>
    VIZ_SELECTORS.getDiscoveryId(state, {}),
  );
  const openDiscovery = useSelector(state =>
    VIZ_SELECTORS.getActive(state, { discoveryId }),
  );

  const isOnReportPage = (): boolean =>
    typeof window !== 'undefined' &&
    window.location.pathname.includes('/open/');
  const tagsOnReport = (isOnReportPage() && openDiscovery?.viz?.tags) || [];
  const tags = _.isEmpty(tagsOnReport) ? availableTags : tagsOnReport;
  const modifiedData = {
    visualizations: data?.visualizations?.map(el => ({
      ...el,
      chartTypes: [el.chartType],
      datasets: data?.datasets?.reduce((acc, { name, id }) => {
        if (id === el.datasetId) {
          return [...acc, name];
        }
        return acc;
      }, []),
    })),
  };

  return (
    <FilterWidgetContext.Provider
      value={{
        loading,
        data: modifiedData,
        selectedTypes: {
          tags,
          chartTypes,
          datasets,
        },
        labelDisplayMap,
      }}
    >
      {children}
    </FilterWidgetContext.Provider>
  );
};

export const useFilterWidgetContext = () => useContext(FilterWidgetContext);
